import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Loader2, Search } from "lucide-react";

// Copia la tabella misure da un'altra scheda: ogni valore copiato resta evidenziato finché non viene sistemato
export const copyMeasures = (from, sizes) => from.map((m) => {
  const values = {};
  const copied = {};
  sizes.forEach((z) => { if (m.values?.[z]) { values[z] = m.values[z]; } copied[z] = true; });
  return { point: m.point, values, copied };
});

export const CopyMeasuresDialog = ({ currentId, onCopy }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: sheets = [], isLoading } = useQuery({
    queryKey: ["model-sheets", "tutti"], enabled: open,
    queryFn: async () => (await api.get("/model-sheets", { params: { season: "tutti" } })).data,
  });
  const list = useMemo(() => sheets.filter((s) => s.id !== currentId && (s.tech?.measures || []).some((m) => m.point)
    && `${s.name} ${s.code} ${s.season}`.toLowerCase().includes(q.toLowerCase())), [sheets, q, currentId]);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="tech-copy-measures-btn"><Copy className="mr-1 h-3.5 w-3.5" />Copia da un modello</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="copy-measures-dialog">
          <DialogHeader>
            <DialogTitle>Copia la tabella misure</DialogTitle>
            <DialogDescription>Scegli una scheda simile: le misure copiate restano evidenziate finché non le controlli o le modifichi. La tabella attuale verrà sostituita.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Cerca modello o codice..." data-testid="copy-measures-search" />
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {isLoading && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-primary" />}
            {!isLoading && list.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessuna scheda con tabella misure.</p>}
            {list.map((s) => (
              <button key={s.id} type="button" onClick={() => { onCopy(s); setOpen(false); }} data-testid={`copy-measures-from-${s.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent">
                <span><span className="font-medium">{s.name}</span><span className="ml-2 text-xs text-muted-foreground">{[s.code, s.season].filter(Boolean).join(" · ")}</span></span>
                <span className="shrink-0 text-xs text-muted-foreground">{s.tech.measures.filter((m) => m.point).length} misure · {(s.sizes || []).join(" ")}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
